import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import TopMenu from '../TopMenu';
import ResumeEditor from './methods/scratchai/ResumeEditor';

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  'https://localhost:7038';
const DEFAULT_TEMPLATE_ID = 'sidebar-teal';
const DEFAULT_EXPERIENCE_LEVEL = 'Fresher';

const toInternalExperience = (value) => {
  if (!value) return 'fresher';
  const normalized = String(value).trim().toLowerCase();
  if (normalized === 'student') return 'student';
  if (normalized === 'fresher') return 'fresher';
  if (normalized === 'experienced') return 'experienced';
  return normalized;
};

const toApiExperience = (value) => {
  const normalized = toInternalExperience(value);
  if (normalized === 'student') return 'Student';
  if (normalized === 'fresher') return 'Fresher';
  if (normalized === 'experienced') return 'Experienced';
  return DEFAULT_EXPERIENCE_LEVEL;
};

const safeParseJson = (value) => {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const unwrapResumePayload = (payload) => {
  const root = payload?.data ?? payload?.resume ?? payload?.result ?? payload;
  return {
    resumeId: root?.resumeId ?? root?.id ?? null,
    templateId: root?.templateSlug ?? root?.templateId ?? root?.template ?? null,
    styleConfigJson: root?.styleConfigJson ?? root?.styleConfig ?? null,
    experienceLevel: root?.experienceLevel ?? root?.experience ?? null,
    studying: root?.currentlyStudying ?? root?.studying ?? null,
    industry: root?.industry ?? null,
    atsScore: root?.atsOverallScore ?? root?.atsScore ?? 0,
    prefillData:
      root?.prefillData
      ?? root?.prefilledData
      ?? root?.formData
      ?? root?.parsedData
      ?? root?.resumeData
      ?? root?.data
      ?? root
      ?? null,
  };
};

const ResumeEditorPage = ({ theme, toggleTheme }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { resumeId: resumeIdParam } = useParams();
  const navState = location.state || {};

  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const resumeIdFromRoute = resumeIdParam || navState.resumeId || query.get('resumeId') || query.get('id') || null;
  const templateIdFromRoute = navState.templateId || query.get('templateId') || query.get('templateSlug') || null;
  const styleConfigJsonFromRoute =
    navState.styleConfigJson
    || query.get('styleConfigJson')
    || null;
  const prefillDataFromRoute = navState.prefillData || safeParseJson(query.get('prefillData')) || null;
  const experienceFromRoute = navState.experience || query.get('experience') || query.get('experienceLevel') || null;
  const studyingFromRoute = navState.studying ?? safeParseJson(query.get('studying')) ?? null;
  const industryFromRoute = navState.industry || query.get('industry') || null;
  const backTarget = navState.backTo || query.get('backTo') || '/resume-builder';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editorData, setEditorData] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const initEditor = async () => {
      setLoading(true);
      setError('');

      try {
        const token = localStorage.getItem('token');
        const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

        let resolved = null;

        if (resumeIdFromRoute) {
          const res = await fetch(`${API_BASE_URL}/api/resume-builder/${resumeIdFromRoute}`, {
            headers: authHeaders,
          });

          if (res.status === 401) {
            navigate('/login', { replace: true });
            return;
          }

          if (!res.ok) {
            throw new Error('Could not load the selected resume.');
          }

          resolved = unwrapResumePayload(await res.json());
        } else {
          const res = await fetch(`${API_BASE_URL}/api/resume-builder/initialise`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...authHeaders,
            },
            body: JSON.stringify({
              templateSlug: templateIdFromRoute || DEFAULT_TEMPLATE_ID,
              experienceLevel: toApiExperience(experienceFromRoute),
              industry: industryFromRoute || null,
            }),
          });

          if (res.status === 401) {
            navigate('/login', { replace: true });
            return;
          }

          if (!res.ok) {
            throw new Error('Could not start a new resume.');
          }

          resolved = unwrapResumePayload(await res.json());
        }

        if (cancelled) return;

        const resolvedTemplateId =
          templateIdFromRoute
          || resolved?.templateId
          || DEFAULT_TEMPLATE_ID;

        const resolvedStyleConfigJson =
          styleConfigJsonFromRoute
          || resolved?.styleConfigJson
          || null;

        const resolvedPrefillData =
          prefillDataFromRoute
          || resolved?.prefillData
          || null;

        const resolvedExperience = toInternalExperience(
          experienceFromRoute || resolved?.experienceLevel || DEFAULT_EXPERIENCE_LEVEL,
        );

        setEditorData({
          resumeId: resolved?.resumeId || resumeIdFromRoute || null,
          templateId: resolvedTemplateId,
          styleConfigJson: resolvedStyleConfigJson,
          prefillData: resolvedPrefillData,
          experience: resolvedExperience,
          studying: studyingFromRoute ?? resolved?.studying ?? null,
          industry: industryFromRoute || resolved?.industry || null,
          atsScore: resolved?.atsScore ?? 0,
        });
      } catch (err) {
        if (!cancelled) {
          console.error(err);

          // If we already have a resumeId, let the editor open with whatever
          // state we have rather than blocking the user entirely.
          if (resumeIdFromRoute) {
            setEditorData({
              resumeId: resumeIdFromRoute,
              templateId: templateIdFromRoute || DEFAULT_TEMPLATE_ID,
              styleConfigJson: styleConfigJsonFromRoute || null,
              prefillData: prefillDataFromRoute || null,
              experience: toInternalExperience(experienceFromRoute || DEFAULT_EXPERIENCE_LEVEL),
              studying: studyingFromRoute ?? null,
              industry: industryFromRoute || null,
              atsScore: 0,
            });
            setError('Could not refresh the resume data. Opened with the saved resumeId.');
          } else {
            setError(err?.message || 'Could not open the resume editor.');
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    initEditor();

    return () => {
      cancelled = true;
    };
  }, [
    experienceFromRoute,
    industryFromRoute,
    navigate,
    prefillDataFromRoute,
    resumeIdFromRoute,
    studyingFromRoute,
    styleConfigJsonFromRoute,
    templateIdFromRoute,
  ]);

  const handleBack = () => {
    navigate(backTarget);
  };

  if (loading) {
    return (
      <div className={`rb-shell ${theme}`} data-theme={theme}>
        <TopMenu theme={theme} toggleTheme={toggleTheme} />
        <div style={{ minHeight: '70vh', display: 'grid', placeItems: 'center', color: '#fff', padding: 24, textAlign: 'center' }}>
          <div>
            <div className="rb-spinner" />
            <p style={{ marginTop: 16 }}>Loading resume editor...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !editorData) {
    return (
      <div className={`rb-shell ${theme}`} data-theme={theme}>
        <TopMenu theme={theme} toggleTheme={toggleTheme} />
        <div style={{ minHeight: '70vh', display: 'grid', placeItems: 'center', color: '#fff', padding: 24, textAlign: 'center' }}>
          <div>
            <p style={{ marginBottom: 12 }}>{error}</p>
            <button
              type="button"
              onClick={handleBack}
              style={{
                padding: '10px 18px',
                borderRadius: 999,
                border: 'none',
                background: '#1bbc8b',
                color: '#fff',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Go back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {error && (
        <div className="rb-error-bar">
          {error}
        </div>
      )}
      <ResumeEditor
        theme={theme}
        toggleTheme={toggleTheme}
        templateId={editorData.templateId}
        styleConfigJson={editorData.styleConfigJson}
        experience={editorData.experience}
        studying={editorData.studying}
        industry={editorData.industry}
        resumeId={editorData.resumeId}
        prefillData={editorData.prefillData}
        atsScore={editorData.atsScore}
        onBack={handleBack}
      />
    </>
  );
};

export default ResumeEditorPage;
